import { isNetworkError } from './apiError'

/*
  Sinal do agendamento online. Como em `services/Banking`, aqui só mora o que é
  compartilhado entre o passo de pagamento do link público e a configuração do salão —
  as chamadas HTTP ficam nas telas, como no resto do app.

  `services/Payment` é sobre pagamento de comanda, dentro do salão. Não confundir: lá o
  dinheiro já entrou e está sendo registrado, aqui ele ainda vai entrar.
*/
export default class OnlinePayment {
  static METHOD_LABELS = {
    pix: 'Pix',
    credit_card: 'Cartão de crédito'
  }

  /*
    `expired` é do Pix: o QR do Asaas tem validade, e depois dela a cobrança não some — ela
    fica pendente para sempre do lado de lá. Quem separa uma coisa da outra é este status,
    que é o que decide entre esperar o pagamento e oferecer um QR novo.
  */
  static STATUS_LABELS = {
    pending: 'Aguardando pagamento',
    confirmed: 'Pagamento confirmado',
    failed: 'Pagamento não aprovado',
    expired: 'Cobrança expirada',
    refunded: 'Pagamento estornado'
  }

  /* Status em que ainda vale perguntar de novo à API se o dinheiro entrou. */
  static PENDING_STATUSES = ['pending', 'processing']

  static getMethodLabel (method) {
    return this.METHOD_LABELS[method] ?? 'Pagamento'
  }

  static getStatusLabel (status) {
    return this.STATUS_LABELS[status] ?? 'Situação desconhecida'
  }

  static getStatusColor (status) {
    if (status === 'confirmed') return '#44CF6C'
    if (['failed', 'expired', 'refunded'].includes (status)) return '#FF3939'
    return '#FF9800'
  }

  static isPending (status) {
    return this.PENDING_STATUSES.includes (status)
  }

  /**
   * Quanto custa o que foi agendado.
   *
   * É a soma e não o preço de um serviço só porque uma escolha de horário pode virar dois
   * agendamentos: quando o serviço não cabe inteiro na agenda de um profissional, o
   * `TimeTablesForm` oferece combinações e o fluxo cria os dois.
   */
  static getTotalValue (schedules = []) {
    return schedules.reduce ((total, schedule) => total + this.getSchedulePrice (schedule), 0)
  }

  /**
   * O preço de um agendamento, na mesma ordem que a API usa para cobrar.
   *
   * `schedule.price` primeiro, e não `service.price`, por um motivo concreto: o preço de um
   * serviço é por salão e vive no vínculo, não na tabela de serviços. Serviço cadastrado
   * assim tem `services.price` **zerado** — "Pé e mão" custa R$ 70,00 no salão e trazia
   * zero aqui, e a tela anunciava "Total dos serviços R$ 0,00" para um atendimento real.
   *
   * `schedule.price` é o preço com que o agendamento nasceu, já resolvido pela API a partir
   * do vínculo e de eventual promoção do dia — exatamente o número que `Deposit/Quote` soma
   * do outro lado. Usando o mesmo campo, estimativa e cobrança deixam de poder divergir.
   *
   * O `service.price` fica como reserva para o caso de a resposta não trazer o preço.
   */
  static getSchedulePrice (schedule = {}) {
    return Number (schedule.price ?? schedule.service?.price ?? 0)
  }

  /**
   * Valor do sinal, **para exibição**.
   *
   * Quem decide o quanto será cobrado é a API — o front nunca manda valor. Isto existe para
   * o cliente saber o que vai pagar antes de escolher pagar, e o arredondamento é para
   * centavo para que o número na tela seja o mesmo que ele vai ver na cobrança.
   */
  static getDepositValue (schedules = [], percentage = 0) {
    return Math.round (this.getTotalValue (schedules) * percentage) / 100
  }

  static getRemainingValue (schedules = [], percentage = 0) {
    return this.getTotalValue (schedules) - this.getDepositValue (schedules, percentage)
  }

  /*
    "MM/AA" como o cliente digita, para o mês e o ano de quatro dígitos que o Asaas espera.
    O ano vai com o século preenchido: `expiryYear: "26"` é recusado.
  */
  static parseCardExpiry (expiry = '') {
    const digits = expiry.replace (/\D/g, '')

    if (digits.length < 4) return { expiryMonth: '', expiryYear: '' }

    return {
      expiryMonth: digits.slice (0, 2),
      expiryYear: `20${digits.slice (2, 4)}`
    }
  }

  static newIdempotencyKey () {
    /*
      `randomUUID` exige contexto seguro, então não existe quando o site é aberto por HTTP
      nem em parte dos ambientes de teste. O reserva não precisa ser criptográfico: a chave
      só distingue tentativas do mesmo navegador.
    */
    const uuid = typeof window !== 'undefined'
      ? window.crypto?.randomUUID?.()
      : null

    return uuid ?? `${Date.now ()}-${Math.random ().toString (36).slice (2)}`
  }

  /**
   * Se a tentativa seguinte pode reusar a chave de idempotência da anterior.
   *
   * Falha sem resposta é o caso que a chave existe para cobrir: não dá para saber se a
   * cobrança aconteceu, então a repetição precisa ir com a mesma chave para a API devolver
   * a cobrança que já existe em vez de criar outra.
   *
   * Recusa com resposta é o contrário — o cartão não passou, e a próxima tentativa é uma
   * cobrança nova. Reusar a chave ali devolveria a recusa de novo, para sempre.
   */
  static shouldKeepIdempotencyKey (error) {
    return isNetworkError (error)
  }
}
